import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";
import { BRAND } from "@/data/portfolio";
import { useSite } from "@/contexts/SiteContext";
import { fileUrl } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/weddings", label: "Weddings" },
  { to: "/films", label: "Films" },
  { to: "/pre-wedding", label: "Pre-Wedding" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/client-gallery", label: "Client Gallery" },
];

const LIGHT_PAGES = ["/weddings", "/pre-wedding"];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { settings } = useSite() || {};
  const logoUrl = settings?.logoUrl;

  const isLight = LIGHT_PAGES.some(p => pathname === p || pathname.startsWith(p + "/"));

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 48);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [pathname]);

  // On light/cream pages the nav must be dark from the very top
  // (not only after scrolling) so it is never invisible on the hero.
  const onLight = isLight && !open;
  const textCls = onLight ? "text-[#111111]" : "text-[#F5EFE4]";

  return (
    <>
      <header
        data-testid="site-header"
        data-gsap="header"
        className={`header-root ${scrolled ? "scrolled" : ""} ${isLight ? "light-page" : ""}`}
      >
        <div className="max-w-[1380px] mx-auto px-6 md:px-12 flex items-center justify-between h-[5.5rem] md:h-[6.5rem]">

          {/* Logo */}
          <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3 flex-shrink-0">
            {logoUrl ? (
              <div className="flex flex-col items-start gap-[3px]">
                <img src={fileUrl(logoUrl)} alt="Mangalfera Films" className="h-14 md:h-16 w-auto object-contain" />
                <span
                  className={`${onLight ? "text-[rgba(17,17,17,0.6)]" : "text-[rgba(245,239,228,0.65)]"}`}
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "clamp(0.48rem, 0.9vw, 0.58rem)",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.32em",
                  }}
                >
                  Wedding Photography &amp; Films&nbsp;·&nbsp;Ahmedabad
                </span>
              </div>
            ) : (
              <div className={`flex flex-col leading-none ${textCls}`}>
                <span className="font-display text-[1.35rem] md:text-[1.6rem] tracking-wide">Mangalfera Films</span>
                <span className="eyebrow text-[0.58rem] opacity-55 mt-[3px]">Ahmedabad · Gujarat</span>
              </div>
            )}
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `eyebrow text-[0.62rem] transition-colors duration-300 ${textCls} ${
                    isActive ? "!text-[#B8914A]" : "hover:!text-[#B8914A]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}

            {/* Instagram icon */}
            <a
              href={BRAND.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="header-instagram"
              className={`${textCls} hover:!text-[#B8914A] transition-colors duration-300 flex items-center`}
            >
              <Instagram size={17} />
            </a>

            <Link to="/contact" data-testid="header-enquire-btn"
              className="btn-gold-fill !py-[0.7rem] !px-5 text-[0.62rem] whitespace-nowrap">
              Enquire Now
            </Link>
          </nav>

          {/* Mobile right side — Instagram + hamburger */}
          <div className="lg:hidden flex items-center gap-4">
            <a
              href={BRAND.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="header-instagram-mobile"
              className={`${textCls} hover:text-[#B8914A] transition-colors`}
            >
              <Instagram size={18} />
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className={`${textCls} p-1`}
              onClick={() => setOpen(v => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      {open && (
        <div data-testid="mobile-menu" className="mobile-menu lg:hidden">
          <div className="flex flex-col gap-1">
            {NAV.map((n, i) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-[2.8rem] leading-tight py-2 border-b border-[rgba(184,145,74,0.12)] ${
                    isActive ? "text-[#B8914A]" : "text-[#F5EFE4]"
                  }`
                }
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="mt-auto pt-8 flex flex-col gap-4">
            <Link to="/contact" data-testid="mobile-enquire-btn" className="btn-gold-fill self-start">
              Enquire Now
            </Link>
            <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 eyebrow text-[0.6rem] text-[rgba(184,145,74,0.8)]">
              <Instagram size={14} /> {BRAND.instagramHandle}
            </a>
            <div className="eyebrow text-[0.58rem] text-[rgba(184,145,74,0.7)]">
              {BRAND.phone} · {BRAND.email}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
